import { MarkerType, Position, type Edge, type Node } from '@xyflow/react';
import type {
  Subject,
  SubjectEdge,
  SubjectListItem,
} from '../domain/entities/content.entity';

/** Tamanho fixo do nó: o nível de detalhe por zoom troca o conteúdo, nunca a caixa. */
export const NODE_WIDTH = 176;
export const NODE_HEIGHT = 72;
export const ROOT_NODE_HEIGHT = 88;
/** Entre folhas vizinhas, na vertical: cabe a raiz, que é o nó mais alto. */
const SLOT_GAP = ROOT_NODE_HEIGHT + 16;
/** Entre níveis, na horizontal: o vão é por onde a aresta passa. */
const LEVEL_GAP = NODE_WIDTH + 80;
const ISOLATED_X_GAP = NODE_WIDTH + 24;
const ISOLATED_COLUMNS = 8;

/** O assunto que o mapa desenha; a incidência só vem na listagem admin. */
export type GraphSubject = Subject &
  Partial<Pick<SubjectListItem, 'generalIncidence'>>;

export type SubjectRole = 'root' | 'branch' | 'leaf' | 'isolated';

export interface SubjectNodeData extends Record<string, unknown> {
  label: string;
  subjectId: string;
  depth: number;
  role: SubjectRole;
  /** Raiz do pai principal; o próprio id para raiz e solto. */
  rootId: string;
  rootName: string;
  /** Posição da raiz na ordem do layout, que escolhe a cor; `null` para solto. */
  rootIndex: number | null;
  parentCount: number;
  childCount: number;
  /**
   * Incidência geral em 0–100, como vem da listagem admin; `null` é "sem
   * cálculo" (o assunto nasceu depois do último rebuild, ou ele nunca rodou).
   */
  generalIncidence: number | null;
  /** Cor da raiz no tema corrente, posta pela página. */
  color?: string;
  /** Foco por visão ou busca: destaca uns e atenua os demais (opcional). */
  highlighted?: boolean;
  dimmed?: boolean;
}

export interface SubjectGraph {
  nodes: Node<SubjectNodeData>[];
  edges: Edge[];
  /** Raízes na ordem do layout (a mesma que escolhe a cor). */
  roots: { id: string; name: string }[];
  isolatedCount: number;
}

/**
 * Layout em árvore (da esquerda para a direita) do grafo de assuntos: cada
 * nível é uma coluna, e as folhas se empilham na vertical, que é o sentido da
 * rolagem — mil folhas lado a lado davam uma faixa larga demais.
 *
 * O grafo é um DAG — um assunto pode ter vários pais, de propósito
 * (`primum-monolith/docs/reference/content-classification.md`). Para posicionar,
 * cada assunto ganha um **pai principal**: o de menor profundidade, e no empate
 * o primeiro por nome e depois por id. Isso dá uma árvore, desenhada com cada
 * folha num slot e o pai centrado na altura dos filhos, então o filho sempre fica
 * dentro do intervalo do pai. Os outros pais continuam como aresta tracejada.
 * Assunto sem pai nem filho sai da árvore e vai para um bloco em grade à direita
 * do nível mais fundo.
 *
 * Toda ordenação desempata por nome e depois por id: a mesma entrada, em
 * qualquer ordem, dá as mesmas posições — e a mesma cor por raiz.
 *
 * ponytail: árvore arrumada por slots de folha, sem minimizar o cruzamento das
 * arestas secundárias; trocar por elkjs se elas ficarem ilegíveis. Ciclo é
 * barrado no back, e aqui só não trava: quem a BFS não alcança vira raiz.
 *
 * Plano: primum-core-root/docs/plans/in-review/mapa-de-assuntos-legivel.md
 */
export function buildSubjectGraph(
  subjects: GraphSubject[],
  subjectEdges: SubjectEdge[],
): SubjectGraph {
  const byId = new Map(subjects.map((s) => [s.id, s]));
  const order = (a: string, b: string) =>
    byId.get(a)!.name.localeCompare(byId.get(b)!.name) ||
    (a < b ? -1 : a > b ? 1 : 0);

  // Aresta para assunto fora da lista sai antes de contar pai, raiz e solto; par
  // repetido conta uma vez.
  const seen = new Set<string>();
  const validEdges = subjectEdges.filter((e) => {
    const key = `${e.parentId}->${e.childId}`;
    if (seen.has(key) || e.parentId === e.childId) return false;
    if (!byId.has(e.parentId) || !byId.has(e.childId)) return false;
    seen.add(key);
    return true;
  });

  const parents = new Map<string, string[]>();
  const children = new Map<string, string[]>();
  for (const id of byId.keys()) {
    parents.set(id, []);
    children.set(id, []);
  }
  for (const e of validEdges) {
    parents.get(e.childId)!.push(e.parentId);
    children.get(e.parentId)!.push(e.childId);
  }

  const role = (id: string): SubjectRole => {
    const p = parents.get(id)!.length;
    const c = children.get(id)!.length;
    if (p === 0 && c === 0) return 'isolated';
    if (p === 0) return 'root';
    return c === 0 ? 'leaf' : 'branch';
  };

  const ids = [...byId.keys()];
  const isolated = ids.filter((id) => role(id) === 'isolated').sort(order);
  const inTree = ids.filter((id) => role(id) !== 'isolated');

  // Profundidade = menor distância a uma raiz (BFS a partir de todas as raízes).
  const depth = new Map<string, number>();
  const queue = inTree.filter((id) => role(id) === 'root').sort(order);
  for (const id of queue) depth.set(id, 0);
  for (let i = 0; i < queue.length; i++) {
    const n = queue[i];
    for (const c of children.get(n)!) {
      if (depth.has(c)) continue;
      depth.set(c, depth.get(n)! + 1);
      queue.push(c);
    }
  }

  // Pai principal: entre os pais alcançados, o mais raso; desempate nome/id. Ele
  // fica sempre um nível acima, então a árvore de posicionamento não tem ciclo.
  const treeChildren = new Map<string, string[]>(inTree.map((id) => [id, []]));
  const treeRoots: string[] = [];
  for (const id of inTree) {
    const reachable = parents.get(id)!.filter((p) => depth.has(p));
    if (reachable.length === 0 || !depth.has(id)) {
      treeRoots.push(id);
      continue;
    }
    const primary = reachable.sort(
      (a, b) => depth.get(a)! - depth.get(b)! || order(a, b),
    )[0];
    treeChildren.get(primary)!.push(id);
  }
  treeRoots.sort(order);
  for (const list of treeChildren.values()) list.sort(order);

  // Slots de folha: folha ocupa o próximo slot, pai fica no meio dos filhos.
  // `centerY` é o centro do nó: a raiz é mais alta e precisa centrar igual.
  const centerY = new Map<string, number>();
  const level = new Map<string, number>();
  const rootOf = new Map<string, string>();
  const treeOrder: string[] = [];
  let slot = 0;
  let maxLevel = 0;
  const place = (id: string, d: number, rootId: string) => {
    treeOrder.push(id);
    level.set(id, d);
    maxLevel = Math.max(maxLevel, d);
    rootOf.set(id, rootId);
    const kids = treeChildren.get(id)!;
    if (kids.length === 0) {
      centerY.set(id, slot++ * SLOT_GAP);
      return;
    }
    for (const k of kids) place(k, d + 1, rootId);
    centerY.set(
      id,
      (centerY.get(kids[0])! + centerY.get(kids[kids.length - 1])!) / 2,
    );
  };
  for (const r of treeRoots) place(r, 0, r);

  const rootIndex = new Map(treeRoots.map((r, i) => [r, i]));
  const isolatedLeft = slot === 0 ? 0 : (maxLevel + 1) * LEVEL_GAP;

  const node = (id: string, isoIdx: number | null): Node<SubjectNodeData> => {
    const r = role(id);
    const rootId = isoIdx !== null ? id : rootOf.get(id)!;
    const height = r === 'root' ? ROOT_NODE_HEIGHT : NODE_HEIGHT;
    return {
      id,
      type: 'subject',
      width: NODE_WIDTH,
      height,
      position:
        isoIdx !== null
          ? {
              x: isolatedLeft + (isoIdx % ISOLATED_COLUMNS) * ISOLATED_X_GAP,
              y: Math.floor(isoIdx / ISOLATED_COLUMNS) * SLOT_GAP,
            }
          : {
              x: level.get(id)! * LEVEL_GAP,
              y: centerY.get(id)! - height / 2,
            },
      data: {
        label: byId.get(id)!.name,
        subjectId: id,
        depth: isoIdx !== null ? 0 : level.get(id)!,
        role: r,
        rootId,
        rootName: byId.get(rootId)!.name,
        rootIndex: isoIdx !== null ? null : rootIndex.get(rootId)!,
        parentCount: parents.get(id)!.length,
        childCount: children.get(id)!.length,
        generalIncidence: byId.get(id)!.generalIncidence ?? null,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
  };

  const nodes = [
    ...treeOrder.map((id) => node(id, null)),
    ...isolated.map((id, i) => node(id, i)),
  ];

  const primaryEdges = new Set(
    [...treeChildren].flatMap(([p, kids]) => kids.map((k) => `${p}->${k}`)),
  );
  const edges: Edge[] = validEdges.map((e) => {
    const id = `${e.parentId}->${e.childId}`;
    const primary = primaryEdges.has(id);
    return {
      id,
      source: e.parentId,
      target: e.childId,
      markerEnd: { type: MarkerType.ArrowClosed },
      ...(primary ? {} : { style: { strokeDasharray: '6 4', opacity: 0.45 } }),
      data: { primary },
    };
  });

  return {
    nodes,
    edges,
    roots: treeRoots.map((id) => ({ id, name: byId.get(id)!.name })),
    isolatedCount: isolated.length,
  };
}
